import { SystemEntity } from '@erp/shared-types';
import type {
  APInvoiceLineItemSplitProposal,
  APInvoiceLineItemSplitProposalAllocation,
} from '@erp/rpc';

/**
 * The split suggestions of an AP invoice as cards for the Related lines tab
 * (GEN-3198): one card per invoice line whose charges add up to a split, or
 * that several combinations could explain.
 */

export interface SplitProposalLineInput {
  id: string;
  lineNumber: number;
  totalPrice?: string | null;
  splitProposal?: APInvoiceLineItemSplitProposal | null;
}

export type SplitProposalMember =
  APInvoiceLineItemSplitProposalAllocation['members'][number];

export interface SplitProposalCard {
  lineId: string;
  lineNumber: number;
  /** The invoice line total the suggestion adds up to. */
  lineAmount: string;
  status: 'proposed' | 'ambiguous';
  reasoning: string;
  /** The parts of a `proposed` card. Empty when ambiguous. */
  parts: APInvoiceLineItemSplitProposalAllocation[];
  /** How many combinations tie on an `ambiguous` card. */
  alternativeCount: number;
}

/** Cards in invoice line order. Lines without a suggestion have none. */
export function buildSplitProposalCards(
  lines: readonly SplitProposalLineInput[],
): SplitProposalCard[] {
  return [...lines]
    .sort((a, b) => a.lineNumber - b.lineNumber)
    .flatMap((line): SplitProposalCard[] => {
      const proposal = line.splitProposal;
      if (!proposal) return [];
      if (proposal.status === 'proposed' && !proposal.parts?.length) return [];
      return [
        {
          lineId: line.id,
          lineNumber: line.lineNumber,
          lineAmount: proposal.lineAmount,
          status: proposal.status,
          reasoning: proposal.reasoning,
          parts: proposal.status === 'proposed' ? (proposal.parts ?? []) : [],
          alternativeCount: proposal.alternatives?.length ?? 0,
        },
      ];
    });
}

/** The charge that carries a part's amount. */
export function primaryMember(
  part: APInvoiceLineItemSplitProposalAllocation,
): SplitProposalMember | undefined {
  return part.members.find((member) => member.role === 'primary');
}

/** The mirrors of the same money on other files (load line, order credit). */
export function companionMembers(
  part: APInvoiceLineItemSplitProposalAllocation,
): SplitProposalMember[] {
  return part.members.filter((member) => member.role === 'companion');
}

/** The document a charge sits on, as the entity the app can link to. */
export function memberEntityType(member: SplitProposalMember): SystemEntity {
  switch (member.source) {
    case 'LOAD':
      return SystemEntity.SCM_SHIPMENT;
    case 'PO':
      return SystemEntity.INTERNAL_PURCHASE_ORDER;
    case 'ORDER':
      return SystemEntity.AR_SALES_ORDER;
  }
}

export interface SplitSuggestionSummary {
  status: SplitProposalCard['status'];
  partCount: number;
}

/** How many invoice lines have a suggestion AP should look at. */
export function countSplitSuggestions(
  lines: readonly SplitProposalLineInput[],
): number {
  return buildSplitProposalCards(lines).length;
}

/** The short form of one line's suggestion, for the Line Items table. */
export function splitSuggestionOf(
  line: SplitProposalLineInput,
): SplitSuggestionSummary | undefined {
  const [card] = buildSplitProposalCards([line]);
  return card && { status: card.status, partCount: card.parts.length };
}
